import React, { useEffect, useState } from 'react';
import {
	XThemeButton,
	XThemeContainer,
	XThemeFilterCards,
	XThemeForm,
	XThemeInputText,
} from '../../styles/GlobalThemeStyle';
import { Row, Col, Space, notification } from 'antd';
import Header from '../../components/Header/Header';
import HeaderFile from '../../components/Header/HeaderFile';
import { InboxOutlined } from '@ant-design/icons';
import {
	XThemeBulkUpload,
	XThemeBulkWrapper,
	XThemeCancelProgressBTN,
	XThemeCardUploading,
	XThemeCSVLottie,
	XThemeErrors,
	XThemePDFProgress,
} from './SimulateEhrStyle';
import { Link, useNavigate } from 'react-router-dom';
import { XLottie } from '../Login/LoginStyle';
import CSVLoadJson from '../../json/csv-loader.json';
import axios from 'axios';
import config from '../../config';
import { getUserToken } from '../../utils/helper';
import {
	validateExcelData,
	formatValidationResults,
	prepareSimulationData,
} from '../../utils/excelValidation';
import { connect } from 'react-redux';
import {
	createSimulateEhrApi,
	createSimulateEhrReset,
} from '../../actions/createSimulateEhrAction';
import {
	XTheme2Button,
	XTheme2MainTitle,
} from '../../styles/GlobalThemeStyleCQ';
import { XTheme2Para1 } from '../Dashboard/DashboardStyleCQ';

const { Dragger } = XThemeBulkUpload;

const CreateSimulateEhr = (props) => {
	const { createSimulateEhr, createSimulateEhrReset, createSimulateEhrState } = props;
	const [form] = XThemeForm.useForm();
	const [simulationName, setSimulationName] = useState('');
	const [excelFile, setExcelFile] = useState([]);
	const [supportingDocs, setSupportingDocs] = useState([]);
	const [validationResults, setValidationResults] = useState(null);
	const [loading, setLoading] = useState(false);
	const [uploading, setUploading] = useState(false);
	const [showLoading, setShowLoading] = useState(false);
	const [uploadProgress, setUploadProgress] = useState(0);
	const navigate = useNavigate();

	const handleExcelChange = async ({ fileList: newFileList }) => {
		setExcelFile(newFileList);

		if (newFileList.length === 0) {
			setValidationResults(null);
			return;
		}

		const file = newFileList[0].originFileObj;

		try {
			setShowLoading(true);
			setLoading(true);

			// Show loading state for 1 second
			await new Promise((resolve) => setTimeout(resolve, 1000));

			const results = await validateExcelData(file);
			const formattedResults = formatValidationResults(results);
			setValidationResults(formattedResults);
			console.log('formattedResults', formattedResults);
			if (formattedResults.summary.errorRows > 0) {
				notification.error({
					message: 'Validation Complete',
					description: `Found ${formattedResults.summary.errorRows} row(s) with errors out of ${formattedResults.summary.totalRows} total rows. Please fix the errors before uploading.`,
					duration: 5,
					key: 'validation-notification',
				});
			}
		} catch (error) {
			notification.error({
				message: 'File Processing Error',
				description:
					error.message ||
					'Failed to process Excel file. Please check the file format.',
				duration: 5,
				key: 'validation-notification',
			});
			setValidationResults(null);
		} finally {
			setLoading(false);
			setShowLoading(false);
		}
	};

	const handleSupportingDocsChange = ({ fileList: newFileList }) => {
		setSupportingDocs(newFileList);
	};

	const handleDownloadSample = () => {
		const link = document.createElement('a');
		link.href = '/EHR Simulation Sample Data.xlsx'; // Sample file for EHR simulations
		link.download = 'EHR Simulation Sample Data.xlsx';
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	};

	const handleUpload = async () => {
		if (!simulationName.trim()) {
			notification.error({
				message: 'Validation Error',
				description: 'Please enter a simulation name',
				key: 'validation-notification',
			});
			return;
		}

		if (excelFile.length === 0) {
			notification.error({
				message: 'Validation Error',
				description: 'Please upload an Excel file',
				key: 'validation-notification',
			});
			return;
		}

		if (
			validationResults?.errorDetails?.length > 0 ||
			!validationResults.summary?.isValid
		) {
			notification.error({
				message: 'Validation Error',
				description:
					'Please ensure Excel file is validated and has no errors',
				key: 'validation-notification',
			});
			return;
		}

		// Check if all required documents from Excel are uploaded
		const uploadedDocNames = new Set(
			supportingDocs.map((doc) => {
				// Remove the file extension for comparison
				const name = doc.name.trim();
				return name.substring(0, name.lastIndexOf('.')) || name;
			}),
		);

		const missingDocs = validationResults.requiredDocuments.filter(
			(docName) => !uploadedDocNames.has(docName.trim()),
		);

		if (missingDocs.length > 0) {
			notification.error({
				message: 'Missing Documents',
				description: `The following documents mentioned in Excel are not uploaded: ${missingDocs.join(
					', ',
				)}`,
				key: 'validation-notification',
			});
			return;
		}

		try {
			setUploading(true);
			const uniqueFolderId = `${Date.now()}_${Math.random()
				.toString(36)
				.substring(2, 15)}`;
			let uploadPromises = [];
			let uploadedFiles = [];

			// Upload Excel file
			const excelFileData = excelFile[0];
			const originalFileName = encodeURIComponent(
				excelFileData.originFileObj.name.trim(),
			);

			const excelRes = await axios.get(
				`${config.api.base_url}/settings/get-signed-url-simulate-ehr?action=putObject&folder_name=batch/${uniqueFolderId}&file_name=${originalFileName}`,
				{
					headers: {
						'Content-type': 'application/json; charset=UTF-8',
						Authorization: 'Bearer ' + getUserToken(),
					},
				},
			);

			uploadPromises.push(
				axios
					.put(excelRes.data.data, excelFileData.originFileObj)
					.then(() => ({
						uid: excelFileData.originFileObj.uid,
						name: excelFileData.originFileObj.name.trim(),
						status: 'done',
						url: `/batch/${uniqueFolderId}/${originalFileName}`,
						type: 'excel',
					})),
			);

			// Upload supporting documents if any
			if (supportingDocs.length > 0) {
				// Simulate progress up to 80%
				const progressInterval = setInterval(() => {
					setUploadProgress((prev) => {
						if (prev >= 80) {
							clearInterval(progressInterval);
							return 80;
						}
						return prev + 5;
					});
				}, 500);

				supportingDocs.forEach((fileData) => {
					const originalDocName = encodeURIComponent(
						fileData.originFileObj.name.trim(),
					);
					const uploadPromise = axios
						.get(
							`${config.api.base_url}/settings/get-signed-url-simulate-ehr?action=putObject&folder_name=batch/${uniqueFolderId}&file_name=${originalDocName}`,
							{
								headers: {
									'Content-type':
										'application/json; charset=UTF-8',
									Authorization: 'Bearer ' + getUserToken(),
								},
							},
						)
						.then((res) =>
							axios
								.put(res.data.data, fileData.originFileObj)
								.then(() => ({
									uid: fileData.originFileObj.uid,
									name: fileData.originFileObj.name.trim(),
									status: 'done',
									url: `/batch/${uniqueFolderId}/${originalDocName}`,
									type: 'supporting',
								})),
						);
					uploadPromises.push(uploadPromise);
				});
			}

			// Wait for all uploads to complete
			const uploadedFilesResults = await Promise.all(uploadPromises);
			uploadedFiles = uploadedFilesResults;

			// Set progress to 100% when all uploads are done
			setUploadProgress(100);

			// Prepare data for API
			const payload = {
				simulation_name: simulationName,
				folder_id: uniqueFolderId,
				excel_url: uploadedFiles.find((file) => file.type === 'excel').url,
				supporting_docs: uploadedFiles
					.filter((file) => file.type === 'supporting')
					.map((file) => file.url),
			};

			// Call API to create simulation
			await createSimulateEhr(payload);
		} catch (error) {
			notification.error({
				message: 'Upload Error',
				description:
					error.message || 'Failed to upload files. Please try again.',
				key: 'upload-notification',
			});
			setUploading(false);
			setUploadProgress(0);
		}
	};

	useEffect(() => {
		if (createSimulateEhrState.apiState === 'success') {
			notification.success({
				message: 'Success',
				description: 'Simulation created successfully',
				key: 'success-notification',
			});

			// Reset form
			setSimulationName('');
			setExcelFile([]);
			setSupportingDocs([]);
			setValidationResults(null);
			form.resetFields();

			// Reset Redux state
			createSimulateEhrReset();

			navigate('/simulate-ehr');
		} else if (createSimulateEhrState.apiState === 'error') {
			notification.error({
				message: 'Error',
				description:
					createSimulateEhrState.error ||
					'Failed to create simulation. Please try again.',
				key: 'error-notification',
			});
		}
	}, [createSimulateEhrState]);

	const handleCancel = () => {
		form.resetFields();
		setSimulationName('');
		setExcelFile([]);
		setSupportingDocs([]);
		setValidationResults(null);
		navigate('/simulate-ehr');
	};

	return (
		<>
			<Col lg={24} xl={24} xs={0} md={0}>
				<Header headerTag="Create Simulation" />
			</Col>
			<Col lg={0} xl={0} xs={24} md={24}>
				<HeaderFile />
			</Col>
			<XThemeContainer fluid>
				<Row gutter={[15, 15]} className="align-items-center mb-4">
					<Col
						xs={24}
						sm={24}
						md={11}
						lg={11}
						xl={11}
						className="text-center text-md-start"
					>
						<XTheme2MainTitle className="mb-2">
							Create EHR Simulation
						</XTheme2MainTitle>
						<XTheme2Para1>
							Create a new EHR simulation by uploading your data.
						</XTheme2Para1>
					</Col>
				</Row>
				<XThemeFilterCards className="mb-3">
					<XThemeForm
						form={form}
						name="simulationForm"
						layout="vertical"
						onFinish={handleUpload}
						autoComplete="off"
					>
						<Row gutter={[15, 15]} className="align-items-center">
							<Col xs={24} sm={12} md={10} lg={6}>
								<XThemeForm.Item
									className="drawerlabelBold"
									label="Simulation Title"
									name="simulationName"
									rules={[
										{
											required: true,
											message: 'Please enter simulation title',
										},
									]}
								>
									<XThemeInputText
										className="largeInputForms"
										size="large"
										placeholder="Enter simulation title"
										value={simulationName}
										onChange={(e) =>
											setSimulationName(e.target.value)
										}
									/>
								</XThemeForm.Item>
							</Col>
						</Row>

						<Row className="align-items-center">
							<Col xs={24}>
								<XThemeForm.Item
									className="drawerlabelBold mb-1"
									label="Excel or CSV Upload"
									name="excelFile"
									rules={[
										{
											required: true,
											message:
												'Please upload an Excel file',
										},
									]}
									extra={
										<div className="text-end mt-1">
											<Link
												to="#"
												onClick={handleDownloadSample}
												style={{
													color: '#007299',
													fontSize: '12px',
												}}
											>
												Download Sample File
											</Link>
										</div>
									}
								>
									<XThemeBulkWrapper>
										<Dragger
											className={`${
												showLoading ? 'd-none' : ''
											} ${
												validationResults?.summary
													?.errorRows > 0
													? 'has-error'
													: ''
											}`}
											accept=".xlsx,.xls,.csv"
											maxCount={1}
											fileList={excelFile}
											onChange={handleExcelChange}
											showUploadList={false}
										>
											{excelFile.length > 0 ? (
												<div>
													<p className="ant-upload-drag-icon">
														<img
															className="img-fluid"
															src="/images/csv-icon.svg"
															alt=""
														/>
													</p>
													<p className="ant-upload-text">
														{excelFile[0].name}
													</p>
													<XThemeCancelProgressBTN
														type="primary"
														onClick={(e) => {
															e.stopPropagation();
															setExcelFile([]);
															setValidationResults(
																null,
															);
														}}
													>
														{validationResults
															?.summary
															?.errorRows > 0
															? 'Delete'
															: 'Upload Again'}
													</XThemeCancelProgressBTN>
												</div>
											) : (
												<>
													<p className="ant-upload-drag-icon">
														<img
															className="img-fluid"
															src="/images/csv-icon.svg"
															alt=""
														/>
													</p>
													<p className="ant-upload-text">
														Drop files or{' '}
														<span>Browse</span>
													</p>
													<p className="ant-upload-hint">
														Format: Excel & CSV Max
														file size: 25 MB
													</p>
												</>
											)}
										</Dragger>

										{validationResults?.summary?.errorRows >
											0 &&
											!showLoading && (
												<XThemeErrors
													className="mt-2"
													type="error"
													message={
														<div>
															<div className="mb-2">
																Found{' '}
																{
																	validationResults
																		.summary
																		.errorRows
																}{' '}
																row(s) with
																errors. Please
																fix and try
																again.
															</div>
															<div
																style={{
																	borderTop:
																		'1px solid rgba(255, 77, 79, 0.2)',
																	paddingTop:
																		'10px',
																	marginTop:
																		'10px',
																}}
															>
																{validationResults.errorDetails.map(
																	(
																		error,
																		index,
																	) => (
																		<div
																			key={
																				index
																			}
																			className="mb-1"
																		>
																			<strong>
																				Row{' '}
																				{
																					error.rowNumber
																				}
																				:
																			</strong>
																			<ul
																				style={{
																					marginBottom:
																						index ===
																						validationResults
																							.errorDetails
																							.length -
																							1
																							? 0
																							: '5px',
																					paddingLeft:
																						'20px',
																					listStyle:
																						'disc',
																				}}
																			>
																				{error.errors.map(
																					(
																						err,
																						errIndex,
																					) => (
																						<li
																							key={
																								errIndex
																							}
																						>
																							{
																								err
																							}
																						</li>
																					),
																				)}
																			</ul>
																		</div>
																	),
																)}
															</div>
														</div>
													}
												/>
											)}

										{validationResults?.summary?.isValid &&
											!showLoading && (
												<XThemeErrors
													className="mt-2"
													type="success"
													message={`Validation successful! ${validationResults.summary.totalRows} rows processed.`}
												/>
											)}

										{showLoading && (
											<XThemeCardUploading>
												<XThemeCSVLottie
													animationData={
														CSVLoadJson
													}
													loop={true}
												/>
												<p className="title">
													Validating Excel file...
												</p>
											</XThemeCardUploading>
										)}
									</XThemeBulkWrapper>
								</XThemeForm.Item>
							</Col>
						</Row>

						<Row className="align-items-center">
							<Col xs={24}>
								<XThemeForm.Item
									className="drawerlabelBold"
									label="Supporting Documents"
									name="supportingDocs"
								>
									<XThemeBulkWrapper>
										<Dragger
											className={
												uploading &&
												supportingDocs.length > 0
													? 'd-none'
													: ''
											}
											accept=".pdf"
											multiple={true}
											fileList={supportingDocs}
											onChange={handleSupportingDocsChange}
											showUploadList={false}
										>
											{supportingDocs.length > 0 ? (
												<div>
													<p className="ant-upload-drag-icon">
														<img
															className="img-fluid"
															src="/images/pdf-upload-icon.svg"
															alt=""
														/>
													</p>
													<p className="ant-upload-text">{`Added ${supportingDocs.length} files`}</p>
													<XThemeCancelProgressBTN
														type="primary"
														onClick={(e) => {
															e.stopPropagation();
															setSupportingDocs([]);
														}}
													>
														Delete
													</XThemeCancelProgressBTN>
												</div>
											) : (
												<>
													<p className="ant-upload-drag-icon">
														<img
															className="img-fluid"
															src="/images/pdf-upload-icon.svg"
															alt=""
														/>
													</p>
													<p className="ant-upload-text">
														Drop files or{' '}
														<span>Browse</span>
													</p>
													<p className="ant-upload-hint">
														Format: PDF Max file size: 25 MB
													</p>
												</>
											)}
										</Dragger>

										{uploading && supportingDocs.length > 0 && (
											<XThemeCardUploading>
												<p className="title mb-1">
													Uploading Supporting Documents...
												</p>
												<Row gutter={[15, 15]}>
													<Col
														xs={{
															span: 24,
															offset: 0,
														}}
														sm={{
															span: 16,
															offset: 4,
														}}
														lg={{
															span: 8,
															offset: 8,
														}}
													>
														<XThemePDFProgress
															percent={uploadProgress}
															format={() => ''}
														/>
														<p className="title2">
															{uploadProgress}% • Uploading...
														</p>
														<XThemeCancelProgressBTN
															className="mt-2"
															type="primary"
															onClick={() => {
																setSupportingDocs([]);
																setUploading(false);
																setUploadProgress(0);
															}}
														>
															Cancel
														</XThemeCancelProgressBTN>
													</Col>
												</Row>
											</XThemeCardUploading>
										)}
									</XThemeBulkWrapper>
								</XThemeForm.Item>
							</Col>
						</Row>

						<Row gutter={[15, 15]}>
							<Col xs={24} className="text-center text-sm-end">
								<Space>
									<XTheme2Button
										size="large"
										className="noBackground px-4"
										onClick={handleCancel}
										disabled={uploading}
									>
										Cancel
									</XTheme2Button>
									<XTheme2Button
										size="large"
										className="px-4"
										htmlType="submit"
										type="primary"
										loading={uploading}
										disabled={
											uploading ||
											loading ||
											!validationResults?.summary
												?.isValid
										}
									>
										Create Simulation
									</XTheme2Button>
								</Space>
							</Col>
						</Row>
					</XThemeForm>
				</XThemeFilterCards>

				{uploading && (
					<Row className="mt-4">
						<Col span={24}>
							<XThemeCardUploading>
								<XThemeCSVLottie
									animationData={CSVLoadJson}
									loop={true}
								/>
								<p className="title">
									Uploading files...{' '}
									<span>{uploadProgress}%</span>
								</p>
								<p className="title2">
									Please wait while we upload your files
								</p>
								<XThemePDFProgress
									percent={uploadProgress}
									status="active"
								/>
								<XThemeCancelProgressBTN
									onClick={() => {
										setUploading(false);
										setUploadProgress(0);
									}}
								>
									Cancel
								</XThemeCancelProgressBTN>
							</XThemeCardUploading>
						</Col>
					</Row>
				)}
			</XThemeContainer>
		</>
	);
};

const mapStateToProps = (state) => ({
	createSimulateEhrState: state.createSimulateEhr,
});

const mapDispatchToProps = (dispatch) => ({
	createSimulateEhr: (data) => dispatch(createSimulateEhrApi(data)),
	createSimulateEhrReset: () => dispatch(createSimulateEhrReset()),
});

export default connect(mapStateToProps, mapDispatchToProps)(CreateSimulateEhr); 